/**
 * The concrete feature flag map that we are going to use to determine if a feature is enabled or not.
 */
const FeatureFlagsMap = {
  'VEGA_DATE_PICKER.COMPONENT_LOAD_REQUIRED_DECORATOR': {
    description: `Feature flag to enable the required decorator for the date picker component. <br/> <b>Note: This feature flag is for demonstration purpose and will be persisted permanently.</b>`,
    createdTimestamp: 1660330561422,
    createdAtVersion: '1.2.0',
  },
  'VEGA_COMPONENT.SANITIZE_VEGA_COMPONENT': {
    description: `Feature flag to enable sanitize vega components.`,
    createdTimestamp: 1664262689486,
    createdAtVersion: '1.8.1',
  },
  'VEGA_TEXT.LABEL_OR_CONTENT_NESTED_VEGA_TEXT_COMPONENT': {
    description: 'Feature flag to enable the label or content of other component nested vega-text component.',
    createdTimestamp: 1665218525140,
    createdAtVersion: '1.8.1',
  },
  'VEGA_FLEX.ENABLE_DIRECTION_ONLY': {
    description: `Feature flag to make direction take effect only regardless of breakpoint for vega-flex component.`,
    createdTimestamp: 1665639912117,
    createdAtVersion: '1.9.0',
  },
  'VEGA_FIELD_LABEL.SHOW_LABEL': {
    description: `Feature flag to enable the vega-field-label component for vega-input, vega-input-select and vega-date-picker.`,
    createdTimestamp: 1666184962131,
    createdAtVersion: '1.9.0',
  },
  'VEGA_FLEX.USE_NATIVE_FLEX_BEHAVIOR': {
    description: `Feature flag to enable the **default** use of native flex behavior for vega-flex component.`,
    createdTimestamp: 1668059639449,
    createdAtVersion: '1.11.1',
  },
  'VEGA_TABLE.DEPRECATED_ROW_SELECTION_CHANGE_PROP': {
    description: `Feature flag to deprecate the rowSelection.onSelectChange and rowSelection.onSelectAllChange`,
    createdTimestamp: 1673321949321,
    createdAtVersion: '1.18.0',
  },
  'VEGA_ICON.USE_SIZE_PROP_FOR_THIRD_PARTY_ICONS': {
    description: `Feature flag to make size prop of vega-icon to take effect for third party icons.`,
    createdTimestamp: 1675752744795,
    createdAtVersion: '1.20.0',
  },
  'VEGA_INJECT_FORM_FIELD_CONTROLLER.ADD_DATA_VEGA_FORM_FILTER': {
    description: `Feature flag to deprecate the form field should validate only have data-vega-from attribute`,
    createdTimestamp: 1676017004107,
    createdAtVersion: '1.19.0',
  },
  'VEGA_FORM_FIELD.CHANGE_BLOCK_TO_INLINE_BLOCK': {
    description: `Feature flag to deprecate the form fields like input/datePicker/timePicker should use inline block`,
    createdTimestamp: 1676512727310,
    createdAtVersion: '1.20.0',
  },
  'VEGA_CHECKBOX_RADIO_GROUP.FLEX_DIRECTION_COL_DEFAULT': {
    description: `Feature flag to make the checkbox group and radio group prop vegaFlexProp default direction to col`,
    createdTimestamp: 1681171200000,
    createdAtVersion: '1.27.3',
  },
  'VEGA_REACT.PREVENT_SSR': {
    description: `Feature flag to prevent the SSR in vega-react`,
    createdTimestamp: 1679450185124,
    createdAtVersion: '1.25.0',
    disabledByDefault: true,
  },
  'VEGA_STEPPER.INPUT_DISABLED': {
    description: `Feature flag to rename the prop input-editable to input-disabled `,
    createdTimestamp: 1683252081350,
    createdAtVersion: '1.30.0',
  },
  'VEGA_FLEX.ALIGNITEM_JUSTIFYCONTENT_USE_PREFIX_V_INSTEAD_OF_VEGA': {
    description: `Feature flag to render vega-flex prop alignItem and justifyContent classes by using v- instead of vega-`,
    createdTimestamp: 1684128467390,
    createdAtVersion: '1.32.0',
  },
  'VEGA_ANGULAR.USE_JUGGLE_RESIZE_OBSERVER': {
    description: `Feature flag to enable the @juggle/resizeObserver to instead window resizeObserver`,
    createdTimestamp: 1684203399089,
    createdAtVersion: '1.31.0',
  },
  'VEGA_INPUT.FOCUS_ON_INPUT_BY_CLICK_CLEAR_BUTTON': {
    description: `Feature flag to keep the input focused whenever click the clear button`,
    createdTimestamp: 1685513925082,
    createdAtVersion: '1.34.0',
  },
  'VEGA_VUE.EMIT_CAMEL_CASE_EVENT_NAME': {
    description: `Feature flag to emit camel case event name for Vue framework`,
    createdTimestamp: 1688971026741,
    createdAtVersion: '1.37.0',
  },
  'VEGA_ITEM_TOGGLE_ISERROR_DEPRECATED': {
    description: `Feature flag to deprecated the component vega-item-toggle property isError`,
    createdTimestamp: 1687934508330,
    createdAtVersion: '1.36.0',
  },
  'VEGA_BUTTON_LINK.DEPRECATED_EVENTFUL': {
    description: `Feature flag to deprecated eventful property`,
    createdTimestamp: 1689313658898,
    createdAtVersion: '1.36.1',
  },
  'VEGA_DROPDOWN.RENAME_PREFIX_TO_PREFIX_ICON': {
    description: `Feature flag to rename prefix to prefixIcon`,
    createdTimestamp: 1689576347049,
    createdAtVersion: '1.37.0',
  },
};
const FeatureFlagsMap$1 = Object.freeze(FeatureFlagsMap);

/**
 * Feature flag controller to manage feature flags.
 *
 * @class FeatureFlag
 * @property {Set<FeatureFlagKey>} disabledFeatureFlags - Set of feature flags that are enabled.
 */
class FeatureFlagController {
  /**
   * During constructor, It enables all the feature flags given by {@link FeatureFlagsMap} expect default disabled flags.
   */
  constructor() {
    this.enabledFeatureFlagKeySet = new Set();
    const flagKeys = Object.keys(FeatureFlagsMap$1);
    this.enable(...flagKeys.filter((flagKey) => !FeatureFlagsMap$1[flagKey].disabledByDefault));
  }
  /**
   * This function enables the feature flags with the given keys.
   *
   * @param {FeatureFlagKey[]} featureFlagKeys - The feature flags to enable.
   */
  enable(...featureFlagKeys) {
    featureFlagKeys.forEach((featureFlagKey) => {
      this.enabledFeatureFlagKeySet.add(featureFlagKey);
    });
  }
  /**
   * Disable the feature flags with the given keys.
   *
   * @param {FeatureFlagKey[]} featureFlagKeys - The feature flags to disable.
   */
  disable(...featureFlagKeys) {
    featureFlagKeys.forEach((featureFlagKey) => {
      this.enabledFeatureFlagKeySet.delete(featureFlagKey);
    });
  }
  /**
   * If the feature flag key is in the set of enabled feature flag keys, then return true, otherwise return false.
   *
   * @param {FeatureFlagKey} featureFlagKey - The feature flag key to check.
   * @returns {boolean} - True if the feature flag key is in the set of enabled feature flag keys, otherwise false.
   */
  isEnabled(featureFlagKey) {
    return this.enabledFeatureFlagKeySet.has(featureFlagKey);
  }
}
const FeatureFlag = new FeatureFlagController();

export { FeatureFlag as F };
